import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { AssetBadge, Badge, NetworkBadge } from '../ui';
import {
  formatDecimal2,
  formatUsd,
  formatUsdSigned,
  isRawMarketHash,
  shortAddress,
} from '../../lib/format';
import { useMyPmMarkets } from '../../hooks/useApiQuery';
import type {
  ActivityItem as ActivityItemT,
  DepositPayload,
  FeePayload,
  RefPayload,
  TradePayload,
  WithdrawalPayload,
} from '../../lib/types';

interface Props {
  item: ActivityItemT;
  locale?: string;
}

/** Shared row layout — left icon/badge + content + right amount. */
export function ActivityItem({ item, locale = 'en-US' }: Props) {
  switch (item.kind) {
    case 'trade.settled':
    case 'trade.cancelled':
      return <TradeRow item={item} locale={locale} />;
    case 'deposit':
      return <DepositRow item={item} locale={locale} />;
    case 'withdrawal':
      return <WithdrawalRow item={item} locale={locale} />;
    case 'fee':
      return <FeeRow item={item} locale={locale} />;
    case 'ref':
      return <RefRow item={item} locale={locale} />;
  }
}

// ─── shared ────────────────────────────────────────────


function Frame({
  left,
  title,
  subtitle,
  amount,
  amountTone,
  trailing,
}: {
  left: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  amount?: React.ReactNode;
  amountTone?: 'positive' | 'negative' | 'neutral';
  trailing?: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 px-4 py-3.5">
      <div className="shrink-0">{left}</div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-[14px] font-medium text-tg-text">{title}</span>
        </div>
        {/*
         * Subtitle wraps to as many lines as needed — earlier `truncate`
         * cut off useful info ("…pod продолжает работать" → "pod прод…").
         * `break-words` prevents long token-less strings (addresses,
         * tx-hashes) from overflowing horizontally.
         */}
        {subtitle ? (
          <p className="pb-caption mt-0.5 break-words leading-snug">
            {subtitle}
          </p>
        ) : null}
        {trailing}
      </div>
      {amount ? (
        <span
          className={clsx(
            'pb-numeral shrink-0 text-[14px]',
            amountTone === 'positive' && 'text-pb-success',
            amountTone === 'negative' && 'text-pb-danger',
            amountTone === 'neutral' && 'text-tg-text',
          )}
        >
          {amount}
        </span>
      ) : null}
    </div>
  );
}

function timeOnly(iso: string, locale: string): string {
  return new Date(iso).toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ─── trade ─────────────────────────────────────────────


function TradeRow({
  item,
  locale,
}: {
  item: ActivityItemT;
  locale: string;
}) {
  const { t } = useTranslation();
  const pmMarkets = useMyPmMarkets();
  const p = item.payload as unknown as TradePayload;
  const pnl = p.realized_pnl_usdc != null ? Number(p.realized_pnl_usdc) : 0;
  const pmDisplay =
    p.bot_kind === 'polymarket' && p.market_id
      ? pmMarkets.data?.items.find((m) => m.market_id === p.market_id)
          ?.display_name ?? null
      : null;
  const tone =
    item.kind === 'trade.cancelled'
      ? 'neutral'
      : pnl > 0
        ? 'positive'
        : pnl < 0
          ? 'negative'
          : 'neutral';
  const sideUp = /up|long/i.test(p.side);

  return (
    <Frame
      left={
        <span
          className={clsx(
            'flex h-9 w-9 items-center justify-center rounded-full',
            sideUp
              ? 'bg-pb-success-soft text-pb-success'
              : 'bg-[color-mix(in_oklab,var(--pb-danger)_14%,transparent)] text-pb-danger',
          )}
        >
          {sideUp ? '↑' : '↓'}
        </span>
      }
      title={
        <>
          {pmDisplay ??
            (p.market && !isRawMarketHash(p.market)
              ? p.market
              : p.bot_kind === 'hyperliquid'
                ? t('activity.market_hl')
                : t('activity.market_pm'))}
          <Badge tone={sideUp ? 'success' : 'danger'} className="ml-1">
            {p.side.toUpperCase()}
          </Badge>
        </>
      }
      subtitle={
        <>
          {/* Platform tag — disambiguates PM and HL rows when the
              dashboard "Recent" + Activity feed mix them. Reuses the
              same i18n keys the Activity-filter chips use, so the
              naming stays in sync. Leads the line so it reads like a
              narrative ("Polymarket · 21:35 · вход 0.40 …"). */}
          <span className="font-medium text-tg-text">
            {p.bot_kind === 'hyperliquid'
              ? t('activity.platform_hyperliquid')
              : t('activity.platform_polymarket')}
          </span>
          {' · '}
          {timeOnly(p.closed_at ?? item.created_at, locale)}
          {p.entry_price
            ? ` · ${t('activity.trade_entry_short')} ${formatDecimal2(p.entry_price)}`
            : ''}
          {' · '}
          {t('activity.trade_stake_short')} {formatUsd(p.notional_usdc)}
          {item.kind === 'trade.cancelled'
            ? ` · ${t('activity.trade_cancelled')}`
            : ''}
        </>
      }
      amount={
        item.kind === 'trade.cancelled'
          ? '—'
          : formatUsdSigned(pnl)
      }
      amountTone={tone as 'positive' | 'negative' | 'neutral'}
    />
  );
}

// ─── wallet ────────────────────────────────────────────


function DepositRow({ item, locale }: { item: ActivityItemT; locale: string }) {
  const { t } = useTranslation();
  const p = item.payload as unknown as DepositPayload;
  const isDone = p.status === 'confirmed';
  return (
    <Frame
      left={<NetworkBadge chainId={p.chain_id} size={36} />}
      title={
        <span className="inline-flex items-center gap-1.5">
          {t('activity.deposit_title')}
          <AssetBadge symbol={p.asset} size={14} />
          <span className="text-tg-hint">{p.asset}</span>
        </span>
      }
      subtitle={
        <>
          {timeOnly(item.created_at, locale)} · {t(`history.status.${isDone ? 'done' : 'pending'}`)}
        </>
      }
      amount={`+${formatUsd(p.amount_usd)}`}
      amountTone="positive"
    />
  );
}

function WithdrawalRow({
  item,
  locale,
}: {
  item: ActivityItemT;
  locale: string;
}) {
  const { t } = useTranslation();
  const p = item.payload as unknown as WithdrawalPayload;
  const isDone = p.status === 'completed';
  const isFailed = p.status === 'failed' || p.status === 'rejected';
  return (
    <Frame
      left={<NetworkBadge chainId={p.chain_id} size={36} />}
      title={
        <span className="inline-flex items-center gap-1.5">
          {t('activity.withdrawal_title')}
          <AssetBadge symbol={p.asset} size={14} />
          <span className="text-tg-hint">{p.asset}</span>
        </span>
      }
      subtitle={
        <>
          {timeOnly(item.created_at, locale)} ·{' '}
          <span className="font-mono">{shortAddress(p.to_address)}</span> ·{' '}
          {t(
            `history.status.${isDone ? 'done' : isFailed ? 'failed' : 'pending'}`,
          )}
        </>
      }
      amount={`−${formatUsd(p.amount_usd)}`}
      amountTone={isFailed ? 'neutral' : 'negative'}
    />
  );
}

// ─── platform ──────────────────────────────────────────


function FeeRow({ item, locale }: { item: ActivityItemT; locale: string }) {
  const { t } = useTranslation();
  const p = item.payload as unknown as FeePayload;
  return (
    <Frame
      left={
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-pb-paper2 text-tg-hint">
          %
        </span>
      }
      title={t('activity.fee_title', { pct: p.fee_pct_applied })}
      subtitle={
        <>
          {timeOnly(item.created_at, locale)} · {t('activity.fee_subtitle')}
        </>
      }
      amount={`−${formatUsd(p.amount_usd)}`}
      amountTone="negative"
    />
  );
}

function RefRow({ item, locale }: { item: ActivityItemT; locale: string }) {
  const { t } = useTranslation();
  const p = item.payload as unknown as RefPayload;
  const isPaid = p.paid_at !== null;
  return (
    <Frame
      left={
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-pb-accent-soft text-pb-accent">
          ✦
        </span>
      }
      title={t('activity.ref_title', { level: p.level })}
      subtitle={
        <>
          {timeOnly(item.created_at, locale)} ·{' '}
          {isPaid ? t('activity.ref_paid') : t('activity.ref_pending')}
        </>
      }
      amount={`+${formatUsd(p.amount_usd)}`}
      amountTone="positive"
    />
  );
}

